import React, { useCallback, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { S3Modal } from './v3/primitives';
import type { GroupLite } from '../../services/supportApi';
import ChannelAvatarMark from './ChannelAvatarMark';
import { hasChannelAvatar } from './channelAvatar';
import CopyGroupsPicker from './CopyGroupsPicker';
import { copyGroupNames, copyGroupOptions } from './copyGroupsModel';
// Le modali sono condivise con l'area rappresentante, che non passa dalla
// shell admin ne dalla pagina del Centro Ticket: i due fogli scoped (.adm2 /
// .sup3, entrambi portati da S3Modal sulla radice) vanno importati qui.
import '../../styles/admin-v2.css';
import '../../styles/support-v3.css';


interface TransferModalProps {
  /** Canali selezionabili: il chiamante esclude già quello corrente. */
  groups: GroupLite[];
  /**
   * Destinazione preselezionata: la usa il pannello Proprieta del Centro
   * Ticket V3, dove il canale si sceglie prima di aprire la modale. Resta
   * modificabile qui dentro, e il motivo resta comunque obbligatorio.
   */
  initialGroupId?: string;
  busy?: boolean;
  /**
   * Canali in copia (parte A): tutti i canali (anche l'attuale, che può restare
   * in copia). Assente = nessuna scelta «Anche in copia a» (comportamento di prima).
   */
  copyOptions?: GroupLite[];
  /** Id dei canali già in copia: esclusi dalla scelta, mostrati come «Già in copia». */
  currentCopies?: string[];
  onClose: () => void;
  onTransfer: (toGroupId: string, motivo: string, copyGroupIds?: string[]) => void | Promise<void>;
}

// Modal «Trasferisci ticket»: canale destinazione + motivo. Resa V3 (T11).
const TransferModal: React.FC<TransferModalProps> = ({
  groups,
  initialGroupId,
  busy = false,
  onClose,
  onTransfer,
  copyOptions,
  currentCopies = [],
}) => {
  const [groupId, setGroupId] = useState<string>(initialGroupId || '');
  const [motivo, setMotivo] = useState<string>('');
  const [copies, setCopies] = useState<string[]>([]);
  // La destinazione non è mai anche in copia: se viene scelta, esce dalle copie.
  const options = copyOptions ? copyGroupOptions(copyOptions, { responsibleId: groupId || undefined, exclude: currentCopies }) : [];
  const chosen = copies.filter((id) => id !== groupId && options.some((g) => g._id === id));
  // Avatar del canale scelto accanto alla select (le <option> non reggono immagini).
  const selected = groups.find((g) => g._id === groupId);
  const selectedAvatar = hasChannelAvatar(selected) ? selected : null;

  // Mentre il trasferimento e in volo ne Escape ne il click sull'overlay
  // chiudono: sono chiusure distratte, e sparire a meta lascerebbe l'operatore
  // senza sapere dove e finito il ticket. Annulla e la X restano espliciti.
  const requestClose = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);

  return (
    <S3Modal open onClose={requestClose} labelledBy="s3-transfer-title">
      <div className="s3-mod">
        <div className="s3-mod__head">
          <h3 className="s3-h3" id="s3-transfer-title">Trasferisci ticket</h3>
          <button
            type="button"
            className="s3-pill s3-pill--sm s3-pill--icon"
            onClick={onClose}
            aria-label="Chiudi"
          >
            <X size={14} />
          </button>
        </div>

        <div className="s3-mod__body">
          <div className="s3-field">
            <label className="s3-label" htmlFor="s3-transfer-group">
              Canale destinazione
            </label>
            {(() => {
              const select = (
                <select
                  id="s3-transfer-group"
                  className="s3-sel"
                  value={groupId}
                  onChange={(e) => {
                    const next = e.target.value;
                    setGroupId(next);
                    setCopies((c) => c.filter((id) => id !== next));
                  }}
                >
                  <option value="">Seleziona un canale…</option>
                  {groups.map((g) => (
                    <option key={g._id} value={g._id}>
                      {g.nome}
                    </option>
                  ))}
                </select>
              );
              if (!selectedAvatar) return select;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ChannelAvatarMark channel={selectedAvatar} size={24} />
                  <div style={{ flex: 1, minWidth: 0 }}>{select}</div>
                </div>
              );
            })()}
          </div>
          <div className="s3-field">
            <label className="s3-label" htmlFor="s3-transfer-motivo">
              Motivo
            </label>
            <textarea
              id="s3-transfer-motivo"
              className="s3-ta"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              placeholder="Indica il motivo del trasferimento…"
            />
          </div>
          {copyOptions && (
            <CopyGroupsPicker
              options={options}
              value={chosen}
              onChange={setCopies}
              disabled={busy}
              already={copyGroupNames(currentCopies.filter((id) => id !== groupId), copyOptions)}
            />
          )}
        </div>

        <div className="s3-mod__foot">
          <button type="button" className="s3-pill" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="s3-pill s3-pill--primary"
            onClick={() => {
              if (busy || !groupId) return;
              if (chosen.length) onTransfer(groupId, motivo.trim(), chosen);
              else onTransfer(groupId, motivo.trim());
            }}
            disabled={busy || !groupId}
          >
            {busy && <Loader2 size={14} className="s3-spin" />}
            Trasferisci
          </button>
        </div>
      </div>
    </S3Modal>
  );
};

export default TransferModal;
